import { useState, useCallback, useMemo } from 'preact/hooks';
import { usePaginatedList } from '../../hooks/use-paginated-list';
import { getTips, updateTip, deleteTip } from '../../api/tips';
import { Card } from '../../components/card';
import { Badge } from '../../components/badge';
import { Button } from '../../components/button';
import { Select } from '../../components/select';
import { Spinner } from '../../components/spinner';
import { EmptyState } from '../../components/empty-state';
import { Message } from '../../components/message';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { Pagination } from '../../components/pagination';
import { TipForm } from './tip-form';
import { BulkTipForm } from './bulk-tip-form';
import type { Tip } from '../../api/types';

const LIMIT = 50;

const FRAMEWORK_OPTIONS = [
  { value: '', label: 'All Frameworks' },
  { value: 'general', label: 'General' },
  { value: 'halal', label: 'Halal' },
  { value: 'allergen', label: 'Allergen' },
  { value: 'diabetic', label: 'Diabetic' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'heart-healthy', label: 'Heart-healthy' },
  { value: 'keto', label: 'Keto' },
  { value: 'renal', label: 'Renal' },
];

type FormView = 'none' | 'single' | 'bulk' | 'edit';

interface TipsByFramework {
  framework: string;
  tips: Tip[];
}

export function TipsTab() {
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [formView, setFormView] = useState<FormView>('none');
  const [editingTip, setEditingTip] = useState<Tip | null>(null);
  const [actionMsg, setActionMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Tip | null>(null);

  const fetcher = useCallback(async (page: number, _search: string) => {
    const res = await getTips({ page, limit: LIMIT, frameworkId: frameworkFilter || undefined });
    return { items: res.tips, total: res.total };
  }, [frameworkFilter]);

  const list = usePaginatedList<Tip>(fetcher);

  const handleToggle = async (tip: Tip) => {
    try {
      await updateTip(tip.id, { is_active: tip.is_active ? 0 : 1 });
      list.refresh();
    } catch (e) {
      setActionMsg({ text: e instanceof Error ? e.message : 'Toggle failed', type: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTip(deleteTarget.id);
      setDeleteTarget(null);
      setActionMsg({ text: 'Tip deleted', type: 'success' });
      list.refresh();
    } catch (e) {
      setDeleteTarget(null);
      setActionMsg({ text: e instanceof Error ? e.message : 'Delete failed', type: 'error' });
    }
  };

  const handleEdit = (tip: Tip) => {
    setEditingTip(tip);
    setFormView('edit');
  };

  const handleFormSave = () => {
    setFormView('none');
    setEditingTip(null);
    setActionMsg({ text: 'Tip saved', type: 'success' });
    list.refresh();
  };

  const handleFormCancel = () => {
    setFormView('none');
    setEditingTip(null);
  };

  const handleBulkDone = (added: number, failed: number) => {
    setFormView('none');
    setActionMsg({ text: `${added} added, ${failed} failed`, type: added > 0 ? 'success' : 'error' });
    list.refresh();
  };

  // Group tips by framework
  const groupedTips = useMemo((): TipsByFramework[] => {
    const groups = new Map<string, Tip[]>();
    for (const tip of list.items) {
      const fw = tip.framework_id ?? 'General';
      if (!groups.has(fw)) groups.set(fw, []);
      groups.get(fw)!.push(tip);
    }
    const result: TipsByFramework[] = [];
    for (const [framework, tips] of groups) {
      result.push({ framework, tips });
    }
    result.sort((a, b) => a.framework.localeCompare(b.framework));
    return result;
  }, [list.items]);

  return (
    <div>
      {/* Toolbar */}
      <Card>
        <div class="toolbar" style={{ alignItems: 'flex-end' }}>
          <div class="filter-field" style={{ minWidth: '160px', maxWidth: '240px' }}>
            <label>Framework</label>
            <Select
              value={frameworkFilter}
              onChange={(v) => {
                setFrameworkFilter(v);
                list.setPage(1);
              }}
              options={FRAMEWORK_OPTIONS}
            />
          </div>
          <div style={{ flex: 1 }} />
          <div class="toolbar-actions">
            <Button size="sm" onClick={() => setFormView('single')}>
              + Add Tip
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setFormView('bulk')}>
              Bulk Add
            </Button>
          </div>
        </div>
      </Card>

      {actionMsg && (
        <Message text={actionMsg.text} type={actionMsg.type} onDismiss={() => setActionMsg(null)} />
      )}

      {formView === 'single' && (
        <TipForm onSave={handleFormSave} onCancel={handleFormCancel} />
      )}
      {formView === 'edit' && editingTip && (
        <TipForm tip={editingTip} onSave={handleFormSave} onCancel={handleFormCancel} />
      )}
      {formView === 'bulk' && (
        <BulkTipForm onDone={handleBulkDone} onCancel={handleFormCancel} />
      )}

      {/* Framework-grouped cards */}
      {list.loading ? (
        <Card>
          <Spinner text="Loading tips" centered />
        </Card>
      ) : list.error ? (
        <Card>
          <Message text={list.error} type="error" />
        </Card>
      ) : list.items.length === 0 ? (
        <Card>
          <EmptyState icon="inbox" message="No tips found" />
        </Card>
      ) : (
        <>
          {groupedTips.map((group) => (
            <Card key={group.framework}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
                <Badge label={group.framework} variant="info" />
                <span style={{ fontSize: '0.76rem', color: 'var(--text-tertiary)' }}>
                  {group.tips.length} tip{group.tips.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                {group.tips.map((tip) => (
                  <div
                    key={tip.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 'var(--sp-3)',
                      padding: 'var(--sp-3)',
                      background: tip.is_active ? 'var(--bg-inset)' : 'var(--bg-surface)',
                      borderRadius: 'var(--radius-sm)',
                      opacity: tip.is_active ? 1 : 0.6,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{tip.text}</div>
                      <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-1)', alignItems: 'center' }}>
                        <Badge label={tip.is_active ? 'Active' : 'Inactive'} variant={tip.is_active ? 'ok' : 'warn'} />
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                          #{tip.id}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--sp-1)', flexShrink: 0 }}>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(tip)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggle(tip)}>
                        {tip.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => setDeleteTarget(tip)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
          <Pagination
            page={list.page}
            total={list.total}
            limit={LIMIT}
            onPageChange={list.setPage}
          />
        </>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Tip"
        message={`Are you sure you want to delete tip #${deleteTarget?.id}?`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        isDestructive
      />
    </div>
  );
}
